<template>
  <div>
    <ComponentHeader
      title="Provider Roster Report"
      subtitle="Providers per TIN, derived from the practice hierarchy. Generate re-walks the hierarchy and adds anyone new as pending — it never changes a provider already under review."
    >
      <button class="btn-primary-sm" :disabled="loading" @click="generate">
        {{ loading ? 'Generating…' : 'Generate / Refresh' }}
      </button>
    </ComponentHeader>

    <div class="p-5">
    <!-- Error -->
    <div v-if="error" class="mb-4 p-4 bg-red-100 border border-red-300 rounded-lg text-sm text-red-800">
      {{ error }}
    </div>

    <!-- Last generate summary -->
    <div
      v-if="lastRun"
      class="mb-4 p-4 bg-blue-100 border border-blue-300 rounded-lg text-sm text-blue-800"
    >
      Walked {{ lastRun.candidates }} provider–TIN pairs across {{ lastRun.tins }} TINs ·
      <strong>{{ lastRun.created }} new</strong> saved as pending
      <span v-if="lastRun.already_on_file">
        · {{ lastRun.already_on_file }} already on file, left untouched
      </span>
      <span v-if="lastRun.providers_without_npi">
        · {{ lastRun.providers_without_npi }} have no NPI on file
      </span>
    </div>

    <!-- Empty state -->
    <div
      v-if="!report && !initialLoading && !loading && !error"
      class="py-12 text-center text-sm text-gray-500"
    >
      No providers stored for this tenant yet. Click "Generate / Refresh" to derive
      them from the practice hierarchy.
    </div>

    <!-- Loading -->
    <LoadingIndicator
      v-if="loading && !report"
      message="Deriving providers from the practice hierarchy…"
    />

    <template v-if="report">
      <!-- Controls -->
      <div class="flex items-end gap-4 mb-4 flex-wrap">
        <div class="grow max-w-xs">
          <label class="block text-xs font-medium text-gray-700 mb-1">Search</label>
          <input
            v-model="search"
            type="text"
            placeholder="TIN, practice, provider, NPI…"
            class="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-dark focus:border-transparent"
          />
        </div>
        <FilterDropdown
          v-model="statusFilter"
          label="Status"
          :options="statusOptions"
          placeholder="All statuses"
          clearable
        />
        <button
          v-if="search || statusFilter"
          class="text-xs text-blue-600 hover:underline pb-2"
          @click="resetFilters"
        >
          Reset
        </button>
        <button
          v-if="filteredGroups.length"
          class="text-xs text-blue-600 hover:underline pb-2"
          @click="allCollapsed ? expandAll() : collapseAll()"
        >
          {{ allCollapsed ? 'Expand all' : 'Collapse all' }}
        </button>
        <div class="ml-auto flex items-end gap-4 pb-1">
          <span class="text-xs text-gray-600">
            {{ visibleCount.toLocaleString() }} providers · {{ filteredGroups.length.toLocaleString() }} TINs
          </span>
          <ExportMenu :disabled="!visibleCount" :exports="[{ type: 'csv', action: exportCsv }]" />
        </div>
      </div>

      <!-- Table -->
      <div class="border border-gray-200 rounded-lg overflow-auto" style="max-height: calc(100vh - 340px)">
        <table class="w-full text-sm">
          <thead class="sticky top-0 z-10 bg-gray-50 border-b border-gray-200">
            <tr>
              <th
                v-for="col in columns"
                :key="col.key"
                class="px-3 py-2.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider whitespace-nowrap cursor-pointer select-none hover:text-gray-700 transition-colors"
                @click="toggleSort(col.key)"
              >
                {{ col.label }}
                <span v-if="sortKey === col.key" class="ml-0.5 text-primary-500">{{ sortDir === 'asc' ? '↑' : '↓' }}</span>
              </th>
            </tr>
          </thead>
          <tbody>
            <template v-for="group in filteredGroups" :key="group.tin">
              <!-- TIN group header — click to fold/unfold. Providers pile up under
                   a single TIN, so a big practice collapses to one line. -->
              <tr
                class="border-t border-gray-200 bg-gray-50/60 hover:bg-blue-50/40 cursor-pointer"
                @click="toggleGroup(group.tin)"
              >
                <td class="px-4 py-3 font-mono text-xs text-gray-800 whitespace-nowrap align-top">
                  <span class="inline-block w-4 text-gray-500">{{ isCollapsed(group.tin) ? '▸' : '▾' }}</span>
                  {{ group.tin }}
                </td>
                <td class="px-4 py-3 align-top">
                  <router-link
                    v-if="practiceLink(group)"
                    :to="practiceLink(group)"
                    class="text-gray-900 font-medium hover:text-neutral-700 hover:underline"
                    @click.stop
                  >
                    {{ group.practice_name }}
                  </router-link>
                  <span v-else class="text-gray-900 font-medium">{{ group.practice_name }}</span>
                  <div class="flex items-center gap-2 mt-1 flex-wrap">
                    <Tag :label="`${group.count} provider${group.count === 1 ? '' : 's'}`" color="blue" />
                    <button
                      v-if="group.practice_entity"
                      class="text-xs text-gray-600 hover:text-gray-800 hover:underline"
                      @click.stop="$emit('preview-practice', group.practice_entity)"
                    >
                      Practice view →
                    </button>
                  </div>
                </td>
                <td class="px-3 py-3 align-top" />
                <td class="px-3 py-3 align-top" />
                <td class="px-3 py-3 align-top">
                  <div class="flex items-center gap-1.5 flex-wrap">
                    <Tag v-if="group.statusCounts.pending" :label="`${group.statusCounts.pending} pending`" color="amber" />
                    <Tag v-if="group.statusCounts.approved" :label="`${group.statusCounts.approved} confirmed`" color="emerald" />
                    <Tag v-if="group.statusCounts.rejected" :label="`${group.statusCounts.rejected} rejected`" color="red" />
                  </div>
                </td>
              </tr>

              <!-- Provider rows, hidden when the group is folded -->
              <tr
                v-for="row in (isCollapsed(group.tin) ? [] : group.providers)"
                :key="row.id"
                class="hover:bg-blue-50/30"
              >
                <td class="px-4 py-3 align-top" />
                <td class="px-4 py-3 align-top" />
                <td class="px-3 py-3 font-mono text-xs text-gray-800 align-top whitespace-nowrap">
                  {{ row.npi || '—' }}
                </td>
                <td class="px-3 py-3 text-gray-800 align-top">
                  {{ row.provider_name || '(unnamed)' }}
                </td>
                <td class="px-3 py-3 align-top">
                  <Tag :label="statusLabel(row.status)" :color="statusColor(row.status)" />
                  <div v-if="row.source === 'practice_added'" class="mt-1">
                    <Tag label="Added by practice" color="blue" />
                  </div>
                  <div v-if="row.practice_reviewer_name" class="text-xs text-gray-600 mt-1">
                    {{ row.practice_reviewer_name }}
                  </div>
                </td>
              </tr>
            </template>
            <tr v-if="filteredGroups.length === 0">
              <td :colspan="columns.length" class="px-4 py-12 text-center text-sm text-gray-500">
                No providers match your filters. Clear the search or status filter to
                see all {{ (report || []).length.toLocaleString() }} providers.
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </template>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import { canAccessEntity } from '@/utils/access'
import ComponentHeader from '@/components/widgetcomponents/library/ComponentHeader.vue'
import LoadingIndicator from '@/components/widgetcomponents/library/LoadingIndicator.vue'
import FilterDropdown from '@/components/widgetcomponents/library/FilterDropdown.vue'
import Tag from '@/components/widgetcomponents/library/Tag.vue'
import ExportMenu from '@/components/widgetcomponents/library/ExportMenu.vue'

const STATUS_LABELS = {
  pending: 'Pending',
  approved: 'Confirmed',
  rejected: 'Rejected',
}
const STATUS_COLORS = {
  pending: 'amber',
  approved: 'emerald',
  rejected: 'red',
}

export default {
  name: 'ProviderRosterReport',
  components: { ComponentHeader, LoadingIndicator, FilterDropdown, Tag, ExportMenu },
  emits: ['preview-practice'],
  data() {
    return {
      loading: false,
      error: null,
      report: null,
      initialLoading: true,
      lastRun: null,
      search: '',
      statusFilter: null,
      sortKey: 'practice_name',
      sortDir: 'asc',
      // TIN value -> true when folded. Groups default open.
      collapsed: {},
      columns: [
        { key: 'tin', label: 'TIN' },
        { key: 'practice_name', label: 'Practice' },
        { key: 'npi', label: 'NPI' },
        { key: 'provider_name', label: 'Provider' },
        { key: 'status', label: 'Status' },
      ],
      statusOptions: [
        { value: 'pending', label: 'Pending' },
        { value: 'approved', label: 'Confirmed' },
        { value: 'rejected', label: 'Rejected' },
      ],
    }
  },
  computed: {
    filteredGroups() {
      if (!this.report) return []
      let rows = this.report
      if (this.statusFilter) rows = rows.filter(r => r.status === this.statusFilter)
      const q = this.search.trim().toLowerCase()
      if (q) {
        rows = rows.filter(r =>
          String(r.tin).includes(q) ||
          (r.practice_name || '').toLowerCase().includes(q) ||
          (r.provider_name || '').toLowerCase().includes(q) ||
          String(r.npi || '').includes(q)
        )
      }

      // Group by TIN, then sort GROUPS by the chosen column (a group sorts by its
      // smallest value for that column, same as the address report).
      const dir = this.sortDir === 'asc' ? 1 : -1
      const k = this.sortKey

      const groups = []
      const byTin = new Map()
      for (const r of rows) {
        let g = byTin.get(r.tin)
        if (!g) {
          g = { tin: r.tin, practice_name: r.practice_name, practice_entity: r.practice_entity, rows: [] }
          byTin.set(r.tin, g)
          groups.push(g)
        }
        g.rows.push(r)
      }

      const numeric = rows.some(r => typeof r[k] === 'number')
      const keyOf = (g) => {
        const vals = g.rows.map(r => r[k])
        return numeric
          ? Math.min(...vals.map(v => (typeof v === 'number' ? v : Number.NEGATIVE_INFINITY)))
          : vals.map(v => String(v ?? '')).sort()[0]
      }
      const cmp = numeric
        ? (a, b) => dir * (keyOf(a) - keyOf(b))
        : (a, b) => dir * String(keyOf(a)).localeCompare(String(keyOf(b)))

      groups.sort((a, b) => cmp(a, b) || String(a.tin ?? '').localeCompare(String(b.tin ?? '')))

      return groups.map(g => {
        const providers = [...g.rows].sort((a, b) =>
          String(a.provider_name ?? '').localeCompare(String(b.provider_name ?? ''))
        )
        const statusCounts = { pending: 0, approved: 0, rejected: 0 }
        for (const r of providers) {
          if (statusCounts[r.status] !== undefined) statusCounts[r.status]++
        }
        return {
          tin: g.tin,
          practice_name: g.practice_name,
          practice_entity: g.practice_entity,
          providers,
          count: providers.length,
          statusCounts,
        }
      })
    },
    visibleCount() {
      return this.filteredGroups.reduce((n, g) => n + g.count, 0)
    },
    allCollapsed() {
      const groups = this.filteredGroups
      return groups.length > 0 && groups.every(g => this.isCollapsed(g.tin))
    },
  },
  mounted() {
    this.load()
  },
  methods: {
    // Canonical entity route, gated by the same access check the AG-Grid
    // entity-link cell uses. Null when the user cannot open that entity.
    practiceLink(group) {
      if (!group.practice_entity || !canAccessEntity(group.practice_entity)) return null
      return `/entity/${group.practice_entity}`
    },
    isCollapsed(tin) {
      return !!this.collapsed[tin]
    },
    toggleGroup(tin) {
      this.collapsed[tin] = !this.collapsed[tin]
    },
    collapseAll() {
      const next = {}
      for (const g of this.filteredGroups) next[g.tin] = true
      this.collapsed = next
    },
    expandAll() {
      this.collapsed = {}
    },
    statusLabel(status) {
      return STATUS_LABELS[status] || status
    },
    statusColor(status) {
      return STATUS_COLORS[status] || 'gray'
    },
    resetFilters() {
      this.search = ''
      this.statusFilter = null
    },
    toggleSort(key) {
      if (this.sortKey === key) {
        this.sortDir = this.sortDir === 'asc' ? 'desc' : 'asc'
      } else {
        this.sortKey = key
        this.sortDir = 'asc'
      }
    },
    async load() {
      try {
        const { data } = await axios.get('/api/rostering/providers/')
        this.report = data.results || data
      } catch (e) {
        this.error = e.response?.data?.detail || e.response?.data?.error || 'Failed to load providers'
      } finally {
        this.initialLoading = false
      }
    },

    // Re-walk the hierarchy and persist anyone new as pending. Postgres, so fast.
    async generate() {
      this.loading = true
      this.error = null
      try {
        const { data } = await axios.post('/api/rostering/providers/generate/')
        this.lastRun = data
        await this.load()
      } catch (e) {
        this.error = e.response?.data?.detail || e.response?.data?.error || 'Failed to generate'
      } finally {
        this.loading = false
      }
    },
    exportCsv() {
      const headers = this.columns.map(c => c.label)
      const keys = this.columns.map(c => c.key)
      const escape = v => {
        const s = String(v ?? '')
        return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s
      }
      const lines = [headers.join(',')]
      for (const g of this.filteredGroups) {
        for (const r of g.providers) {
          lines.push(keys.map(k => escape(r[k])).join(','))
        }
      }
      const blob = new Blob([lines.join('\n')], { type: 'text/csv' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `provider-roster-${new Date().toISOString().slice(0, 10)}.csv`
      a.click()
      URL.revokeObjectURL(url)
    },
  },
}
</script>
